/**
 * The error envelope, as a thing the UI can branch on.
 *
 * Every failure from the API carries a stable machine-readable `code`. The
 * `message` is display text that may be reworded at any time, so nothing in
 * the app is allowed to match on it — that is the mistake this file exists to
 * prevent. Screens branch on `code`; they render `message`.
 */

/**
 * Codes the console handles specifically. This is a subset, not the full set:
 * the server owns the list, and a code arriving here that is not in this union
 * is normal, not a bug. `ApiError.code` stays a plain string for that reason —
 * narrowing it to a union would turn a new server code into a type error in a
 * client that is merely a version behind.
 */
export const ErrorCode = {
  Unauthenticated: "unauthenticated",
  TokenExpired: "token_expired",
  Forbidden: "forbidden",
  InsufficientScope: "insufficient_scope",
  NotFound: "not_found",
  ValidationError: "validation_error",
  Conflict: "conflict",
  IdempotencyKeyRequired: "idempotency_key_required",
  IdempotencyConflict: "idempotency_conflict",
  InsufficientBalance: "insufficient_balance",
  BalanceCapExceeded: "balance_cap_exceeded",
  /**
   * The plan refused it: no open seat for an invitation, an accept or an SSO
   * join; a seat past the plan's ceiling, or on a plan that sells none; an
   * environment past the plan's environments per project; or a monthly coin
   * limit above the plan's coin ceiling, or on a plan that does not mint. 402.
   */
  PlanLimitReached: "plan_limit_reached",
  CurrencyNotInRelease: "currency_not_in_release",
  NoActiveRelease: "no_active_release",
  RateLimited: "rate_limited",
  ServerError: "server_error",
  Unavailable: "unavailable",
} as const;

/**
 * One refusal from a publish-time validator: what it is about, what is wrong
 * and what to do. Mirrors `Problem` in the control API.
 */
export interface ApiProblem {
  /** A rule's name, a currency's key — the author's words, never a filename. */
  subject: string;
  message: string;
  /** What to do about it. Absent only where the validator had nothing to add. */
  fix?: string;
  /** The document at fault, for the console to open. */
  path?: string;
}

export interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    hint?: string;
    docs_url?: string;
    request_id?: string;
    problems?: ApiProblem[];
  };
}

/**
 * A failed API call.
 *
 * Thrown rather than returned. The alternative — a result object every caller
 * has to unwrap — reads well in isolation and badly in a component tree, where
 * TanStack Query already models "this request failed" as a first-class state.
 * Throwing puts the failure where the framework is already looking for it.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly hint?: string;
  readonly docsUrl?: string;
  /** Paste into the console to land on this exact request. */
  readonly requestId?: string;
  /**
   * Every refusal a publish collected, when the failure was a validation one.
   *
   * This class used to drop them on the floor. The platform's validators are
   * the best copy in the system — "this rule's curve reads \"data.height\",
   * which this event does not promise to carry" — and they arrive in this
   * envelope on a 422, all of them at once by design, because somebody fixing
   * four problems one publish at a time stops trusting the check. Not reading
   * them here meant the publish dialog could only ever show a request id, and
   * the only way to the actual reason was to re-issue the POST by hand.
   */
  readonly problems: ApiProblem[];

  constructor(status: number, envelope: ErrorEnvelope["error"]) {
    super(envelope.message);
    this.name = "ApiError";
    this.status = status;
    this.code = envelope.code;
    this.hint = envelope.hint;
    this.docsUrl = envelope.docs_url;
    this.requestId = envelope.request_id;
    this.problems = envelope.problems ?? [];
  }

  /** True when signing in again would plausibly fix this. */
  get isAuthFailure(): boolean {
    return (
      this.status === 401 ||
      this.code === ErrorCode.Unauthenticated ||
      this.code === ErrorCode.TokenExpired
    );
  }

  /** True when the caller's token is valid but lacks the scope or the role. */
  get isPermissionFailure(): boolean {
    return this.code === ErrorCode.Forbidden || this.code === ErrorCode.InsufficientScope;
  }

  /**
   * Retrying is only safe for failures that are about the moment rather than
   * the request. A 409 means the request conflicts with the world's current
   * state, and repeating it unchanged will conflict again.
   */
  get isRetryable(): boolean {
    return this.status >= 500 || this.code === ErrorCode.RateLimited;
  }
}

/** Narrow an unknown thrown value, since catch blocks receive `unknown`. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Build an ApiError from a response whose body may or may not be the envelope.
 *
 * A proxy, a load balancer or a crash can produce a non-JSON body with any
 * status. Falling back to a synthetic envelope means the UI's error path is
 * uniform: there is no second shape for "the error itself was malformed".
 */
export function toApiError(status: number, body: unknown): ApiError {
  const envelope = body as Partial<ErrorEnvelope> | null | undefined;
  const inner = envelope?.error;
  if (inner && typeof inner.code === "string" && typeof inner.message === "string") {
    return new ApiError(status, inner);
  }
  return new ApiError(status, {
    code: status >= 500 ? ErrorCode.ServerError : ErrorCode.ValidationError,
    message:
      status >= 500
        ? "Something went wrong on our side."
        : "That request could not be completed.",
  });
}
